import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getGoogleMapsApiKey } from '../lib/googleMaps';

interface MiniMapPreviewProps {
  latitude: number;
  longitude: number;
  height?: number;
  zoom?: number;
}

export function MiniMapPreview({ latitude, longitude, height = 128, zoom = 15 }: MiniMapPreviewProps) {
  const { t } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getGoogleMapsApiKey().then((key) => {
      setApiKey(key);
      setLoaded(true);
    });
  }, []);

  if (!loaded) {
    return (
      <div
        className="w-full rounded-lg bg-subtle border border-line flex items-center justify-center text-theme-secondary text-xs"
        style={{ height }}
      >
        {tCommon('status.loading')}
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div
        className="w-full rounded-lg bg-subtle border border-line flex items-center justify-center text-theme-secondary text-xs"
        style={{ height }}
      >
        {t('map.previewUnavailable')}
      </div>
    );
  }

  const src = `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=${zoom}&size=400x${height}&maptype=hybrid&markers=color:red|${latitude},${longitude}&key=${apiKey}`;

  return (
    <div>
      <div
        className="relative w-full rounded-lg overflow-hidden border border-line bg-subtle"
        style={{ height }}
      >
        <img
          src={src}
          alt={t('map.locationMapAlt')}
          className="w-full h-full object-cover"
        />
      </div>
      <a
        href={`https://www.google.com/maps?q=${latitude},${longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1"
      >
        <ExternalLink size={10} />
        {t('map.viewInGoogleMaps')}
      </a>
    </div>
  );
}
